import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ShieldWarning, Scales, Bank, Plus, CaretDown, Check, X as XIcon,
  Eye, Flag, HandCoins, ChartLineUp, Warning, Gavel
} from '@phosphor-icons/react';

const PRIORITY_STYLE = { low: 'text-zinc-400 border-zinc-600', medium: 'text-blue-400 border-blue-700/40', high: 'text-amber-400 border-amber-700/40', critical: 'text-red-400 border-red-700/40' };
const STATUS_STYLE = { open: 'text-blue-400 border-blue-700/40', investigating: 'text-amber-400 border-amber-700/40', escalated: 'text-red-400 border-red-700/40', resolved: 'text-emerald-400 border-emerald-700/40', dismissed: 'text-zinc-400 border-zinc-600' };

export default function ComplianceCenter() {
  const [tab, setTab] = useState('moderation');
  const [modQueue, setModQueue] = useState([]);
  const [modStats, setModStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [holds, setHolds] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [finance, setFinance] = useState(null);
  const [aged, setAged] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewCase, setShowNewCase] = useState(false);
  const [showNewHold, setShowNewHold] = useState(false);
  const [caseForm, setCaseForm] = useState({ title: '', description: '', priority: 'medium', type: 'fraud', subject_email: '' });
  const [holdForm, setHoldForm] = useState({ affiliate_id: '', affiliate_email: '', amount: 0, reason: 'manual_review', reason_detail: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all([
        api.get('/compliance/moderation/queue'),
        api.get('/compliance/moderation/stats'),
        api.get('/compliance/cases'),
        api.get('/compliance/holds'),
        api.get('/compliance/disputes'),
        api.get('/compliance/finance/summary'),
        api.get('/compliance/finance/aged-balances'),
      ]);
      setModQueue(results[0].data || []);
      setModStats(results[1].data);
      setCases(results[2].data || []);
      setHolds(results[3].data || []);
      setDisputes(results[4].data || []);
      setFinance(results[5].data);
      setAged(results[6].data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const reviewMod = async (id, action) => {
    try {
      await api.put(`/compliance/moderation/${id}`, { action });
      toast.success(`Item ${action}d`);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const createCase = async () => {
    if (!caseForm.title) return;
    try {
      await api.post('/compliance/cases', caseForm);
      toast.success('Case created');
      setShowNewCase(false);
      setCaseForm({ title: '', description: '', priority: 'medium', type: 'fraud', subject_email: '' });
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const updateCase = async (id, status) => {
    try {
      await api.put(`/compliance/cases/${id}`, { status });
      toast.success('Case updated');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const createHold = async () => {
    try {
      await api.post('/compliance/holds', holdForm);
      toast.success('Hold created');
      setShowNewHold(false);
      setHoldForm({ affiliate_id: '', affiliate_email: '', amount: 0, reason: 'manual_review', reason_detail: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const resolveHold = async (id, action) => {
    try {
      await api.put(`/compliance/holds/${id}`, { action });
      toast.success(`Hold ${action}d`);
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const TABS = [
    { key: 'moderation', label: 'Moderation', icon: Flag },
    { key: 'cases', label: 'Cases', icon: Gavel },
    { key: 'holds', label: 'Holds & Disputes', icon: HandCoins },
    { key: 'finance', label: 'Finance', icon: Bank },
  ];

  return (
    <div className="space-y-6" data-testid="compliance-center-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Compliance Center</h1>
          <p className="text-sm text-zinc-500 mt-1">Fraud, moderation, finance, and compliance management</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-zinc-800 pb-px overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition-colors whitespace-nowrap ${tab === t.key ? 'border-blue-500 text-blue-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? <div className="text-center text-zinc-500 py-8">Loading...</div> : (
        <>
          {/* MODERATION TAB */}
          {tab === 'moderation' && (
            <div className="space-y-4">
              {modStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-3"><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Pending</p><p className="text-xl font-bold text-amber-400 mt-0.5">{modStats.pending}</p></CardContent></Card>
                  <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-3"><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Last 24h</p><p className="text-xl font-bold mt-0.5">{modStats.last_24h}</p></CardContent></Card>
                  <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-3"><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Total</p><p className="text-xl font-bold mt-0.5">{modStats.total}</p></CardContent></Card>
                  <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-3"><p className="text-[10px] text-zinc-500 uppercase tracking-wider">Types</p><p className="text-xl font-bold mt-0.5">{Object.keys(modStats.by_type || {}).length}</p></CardContent></Card>
                </div>
              )}
              {modQueue.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-8 text-center text-zinc-500">Moderation queue is empty</CardContent></Card>
              ) : (
                <div className="space-y-2">
                  {modQueue.map(item => (
                    <div key={item.moderation_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800" data-testid={`mod-${item.moderation_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] text-zinc-400 border-zinc-600">{item.content_type}</Badge>
                          <p className="text-sm text-zinc-200 truncate">{item.content_title || item.content_id}</p>
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5">{item.reason} &middot; by {item.submitted_by}</p>
                      </div>
                      <div className="flex gap-1 shrink-0 ml-2">
                        <Button size="sm" variant="ghost" onClick={() => reviewMod(item.moderation_id, 'approve')} className="h-7 px-2 text-xs text-emerald-400"><Check size={12} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => reviewMod(item.moderation_id, 'reject')} className="h-7 px-2 text-xs text-red-400"><XIcon size={12} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => reviewMod(item.moderation_id, 'escalate')} className="h-7 px-2 text-xs text-amber-400"><Warning size={12} /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* CASES TAB */}
          {tab === 'cases' && (
            <div className="space-y-4">
              <div className="flex justify-end"><Button size="sm" onClick={() => setShowNewCase(true)} data-testid="new-case-btn" className="bg-blue-600 hover:bg-blue-700"><Plus size={14} className="mr-1" /> New Case</Button></div>
              {showNewCase && (
                <Card className="bg-zinc-900 border-zinc-700">
                  <CardContent className="p-4 space-y-3">
                    <Input data-testid="case-title" value={caseForm.title} onChange={(e) => setCaseForm(p => ({...p, title: e.target.value}))} placeholder="Case title" className="bg-zinc-800 border-zinc-700" />
                    <Input value={caseForm.description} onChange={(e) => setCaseForm(p => ({...p, description: e.target.value}))} placeholder="Description" className="bg-zinc-800 border-zinc-700" />
                    <div className="grid grid-cols-3 gap-3">
                      <div className="relative"><select value={caseForm.priority} onChange={(e) => setCaseForm(p => ({...p, priority: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option></select><CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" /></div>
                      <div className="relative"><select value={caseForm.type} onChange={(e) => setCaseForm(p => ({...p, type: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer"><option value="fraud">Fraud</option><option value="policy">Policy</option><option value="compliance">Compliance</option><option value="abuse">Abuse</option></select><CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" /></div>
                      <Input value={caseForm.subject_email} onChange={(e) => setCaseForm(p => ({...p, subject_email: e.target.value}))} placeholder="Subject email" className="bg-zinc-800 border-zinc-700" />
                    </div>
                    <div className="flex gap-2"><Button size="sm" onClick={createCase} data-testid="save-case-btn" className="bg-blue-600">Create</Button><Button size="sm" variant="ghost" onClick={() => setShowNewCase(false)} className="text-zinc-400">Cancel</Button></div>
                  </CardContent>
                </Card>
              )}
              {cases.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-8">No cases</p>
              ) : (
                <div className="space-y-2">
                  {cases.map(c => (
                    <div key={c.case_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800" data-testid={`case-${c.case_id}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2"><p className="text-sm font-medium text-zinc-200">{c.title}</p><Badge variant="outline" className={`text-[10px] ${PRIORITY_STYLE[c.priority]}`}>{c.priority}</Badge><Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[c.status]}`}>{c.status}</Badge></div>
                        <p className="text-xs text-zinc-500 mt-0.5">{c.type} &middot; {c.subject_email || 'N/A'} &middot; {new Date(c.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        {c.status === 'open' && <Button size="sm" variant="ghost" onClick={() => updateCase(c.case_id, 'investigating')} className="h-7 px-2 text-xs text-amber-400">Investigate</Button>}
                        {c.status === 'investigating' && <Button size="sm" variant="ghost" onClick={() => updateCase(c.case_id, 'resolved')} className="h-7 px-2 text-xs text-emerald-400">Resolve</Button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* HOLDS & DISPUTES TAB */}
          {tab === 'holds' && (
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-3"><h2 className="text-lg font-semibold">Payout Holds</h2><Button size="sm" onClick={() => setShowNewHold(true)} data-testid="new-hold-btn" className="bg-amber-600 hover:bg-amber-700"><Plus size={14} className="mr-1" /> New Hold</Button></div>
                {showNewHold && (
                  <Card className="mb-4 bg-zinc-900 border-zinc-700"><CardContent className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Input data-testid="hold-affiliate" value={holdForm.affiliate_id} onChange={(e) => setHoldForm(p => ({...p, affiliate_id: e.target.value}))} placeholder="Affiliate ID" className="bg-zinc-800 border-zinc-700" />
                      <Input type="number" value={holdForm.amount} onChange={(e) => setHoldForm(p => ({...p, amount: parseFloat(e.target.value) || 0}))} placeholder="Amount" className="bg-zinc-800 border-zinc-700" />
                    </div>
                    <div className="relative"><select value={holdForm.reason} onChange={(e) => setHoldForm(p => ({...p, reason: e.target.value}))} className="w-full h-9 bg-zinc-800 border border-zinc-700 rounded-md px-3 text-sm text-zinc-300 appearance-none cursor-pointer"><option value="fraud_investigation">Fraud Investigation</option><option value="policy_violation">Policy Violation</option><option value="kyc_pending">KYC Pending</option><option value="dispute">Dispute</option><option value="manual_review">Manual Review</option><option value="compliance_check">Compliance Check</option></select><CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" /></div>
                    <div className="flex gap-2"><Button size="sm" onClick={createHold} data-testid="save-hold-btn" className="bg-amber-600">Create Hold</Button><Button size="sm" variant="ghost" onClick={() => setShowNewHold(false)} className="text-zinc-400">Cancel</Button></div>
                  </CardContent></Card>
                )}
                {holds.length === 0 ? <p className="text-sm text-zinc-500">No active holds</p> : (
                  <div className="space-y-2">{holds.map(h => (
                    <div key={h.hold_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-800/30 border border-zinc-800">
                      <div><p className="text-sm text-zinc-200">{h.affiliate_id || h.affiliate_email} — <span className="text-amber-400 font-bold">${h.amount}</span></p><p className="text-xs text-zinc-500">{h.reason?.replace('_', ' ')} &middot; {h.status}</p></div>
                      {h.status === 'active' && <div className="flex gap-1"><Button size="sm" variant="ghost" onClick={() => resolveHold(h.hold_id, 'release')} className="h-7 px-2 text-xs text-emerald-400">Release</Button><Button size="sm" variant="ghost" onClick={() => resolveHold(h.hold_id, 'forfeit')} className="h-7 px-2 text-xs text-red-400">Forfeit</Button></div>}
                    </div>
                  ))}</div>
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold mb-3">Disputes ({disputes.length})</h2>
                {disputes.length === 0 ? <p className="text-sm text-zinc-500">No disputes</p> : (
                  <div className="space-y-2">{disputes.map(d => (
                    <div key={d.dispute_id} className="p-3 rounded-lg bg-zinc-800/30 border border-zinc-800">
                      <div className="flex items-center gap-2"><p className="text-sm text-zinc-200">{d.subject}</p><Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[d.status]}`}>{d.status}</Badge></div>
                      <p className="text-xs text-zinc-500 mt-0.5">{d.filed_by} &middot; ${d.amount_disputed} &middot; {d.related_type}</p>
                    </div>
                  ))}</div>
                )}
              </div>
            </div>
          )}

          {/* FINANCE TAB */}
          {tab === 'finance' && finance && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { label: 'Total Revenue', value: `$${finance.total_revenue?.toLocaleString()}` },
                  { label: 'Total Payouts', value: `$${finance.total_payouts?.toLocaleString()}` },
                  { label: 'Net Revenue', value: `$${finance.net_revenue?.toLocaleString()}` },
                  { label: 'Take Rate', value: `${finance.take_rate}%` },
                  { label: 'Pending Payouts', value: `$${finance.pending_payouts?.toLocaleString()}` },
                  { label: 'Active Holds', value: `$${finance.active_holds?.toLocaleString()}`, color: 'text-amber-400' },
                  { label: 'Open Disputes', value: finance.open_disputes, color: 'text-red-400' },
                  { label: 'Ad Revenue', value: `$${finance.ad_revenue?.toLocaleString()}` },
                  { label: 'Subscriptions', value: finance.active_subscriptions },
                  { label: 'Gross Margin', value: `$${finance.gross_margin?.toLocaleString()}`, color: 'text-emerald-400' },
                ].map((s, i) => (
                  <Card key={i} className="bg-zinc-900/50 border-zinc-800"><CardContent className="p-3"><p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p><p className={`text-lg font-bold mt-0.5 ${s.color || ''}`}>{s.value || 0}</p></CardContent></Card>
                ))}
              </div>
              {aged && (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Outstanding Balances (${aged.total_outstanding?.toLocaleString()})</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-1">
                      {(aged.affiliates || []).slice(0, 10).map((a, i) => (
                        <div key={i} className="flex items-center justify-between text-sm p-2 rounded bg-zinc-800/30">
                          <span className="text-zinc-300">{a.name} ({a.email})</span>
                          <span className="text-amber-400 font-mono font-bold">${a.balance?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
